import { create } from "zustand";

/**
 * Favorites override store.
 *
 * Holds optimistic, client-side overrides for the `is_favorited` state of
 * reports and folders, keyed by resource id. This lets us reflect favorite
 * toggles in the UI **instantly** without re-querying the (heavy, encrypted)
 * list on every click.
 *
 * Flow:
 *  - On toggle (useFavoriteCRUD): set an override with the new value.
 *  - Components read the effective value via `useFavoriteStatus(id, base)`,
 *    which prefers the override over the base value coming from the list data.
 *  - When the list is genuinely re-queried (create/delete/move/rename/filter/
 *    navigation), the fresh server data becomes authoritative and overrides are
 *    cleared via `clearOverrides()` so we never drift from the backend.
 */
interface FavoritesStoreState {
    /** Map of resourceId -> is_favorited (optimistic override). */
    overrides: Record<string, boolean>;
    actions: {
        /** Set/replace the optimistic favorite state for a resource. */
        setOverride: (resourceId: string, isFavorited: boolean) => void;
        /** Remove a single override (e.g. on mutation rollback). */
        removeOverride: (resourceId: string) => void;
        /**
         * Drop overrides for the given resource ids only. Used when fresh list
         * data arrives: the listing is authoritative for the items it contains,
         * but overrides for resources NOT in the listing (e.g. the parent folder
         * you just opened) must be preserved.
         */
        clearOverridesFor: (resourceIds: string[]) => void;
        /** Drop every override. */
        clearOverrides: () => void;
    };
}

export const useFavoritesStore = create<FavoritesStoreState>((set) => ({
    overrides: {},
    actions: {
        setOverride: (resourceId, isFavorited) =>
            set((state) => ({
                overrides: { ...state.overrides, [resourceId]: isFavorited },
            })),
        removeOverride: (resourceId) =>
            set((state) => {
                if (!(resourceId in state.overrides)) return state;
                const next = { ...state.overrides };
                delete next[resourceId];
                return { overrides: next };
            }),
        clearOverridesFor: (resourceIds) =>
            set((state) => {
                if (resourceIds.length === 0) return state;
                let changed = false;
                const next = { ...state.overrides };
                for (const id of resourceIds) {
                    if (id in next) {
                        delete next[id];
                        changed = true;
                    }
                }
                return changed ? { overrides: next } : state;
            }),
        clearOverrides: () =>
            set((state) =>
                Object.keys(state.overrides).length === 0
                    ? state
                    : { overrides: {} }
            ),
    },
}));

export const useFavoritesActions = () => useFavoritesStore((state) => state.actions);

/**
 * Reactive selector returning the effective favorite state for a resource:
 * the optimistic override when present, otherwise the provided base value.
 */
export const useFavoriteStatus = (
    resourceId: string | undefined,
    base: boolean
): boolean =>
    useFavoritesStore((state) =>
        resourceId !== undefined && resourceId in state.overrides
            ? state.overrides[resourceId]
            : base
    );
